import { BRAND_COLOR_SLOTS, type BrandColorSlot, type BrandKit } from '../brand';
import { createLayer, uniqueLayerName } from '../factory';
import { BUILTIN_FONTS } from '../fonts';
import { GALLERY_SRC_PREFIX } from '../gallery/illustrations';
import { PHOTOS } from '../gallery/photos';
import type { LocaleId } from '../locales';
import { applyAutoSfx } from '../motion/auto-sfx';
import { CAMERA_PRESET_MAP, CAMERA_PRESETS } from '../motion/camera';
import { sceneTimeline } from '../motion/compile';
import { FX_KIND_MAP, FX_KINDS, TEXT_EFFECT_KINDS, type FxKind } from '../motion/fx';
import { MUSIC_ENERGIES, MUSIC_STYLES, type MusicEnergy, type MusicStyle } from '../motion/music';
import {
  EMPHASIS_PRESET_MAP,
  EMPHASIS_PRESETS,
  ENTER_PRESET_MAP,
  ENTER_PRESETS,
} from '../motion/presets';
import type { DocMotion, LayerMotion, MotionScene, MotionSplit } from '../motion/schema';
import { SPLIT_UNITS } from '../motion/schema';
import { TRANSITION_MAP, TRANSITIONS } from '../motion/transitions';
import {
  HEX_COLOR_RE,
  SHAPES,
  templateDocSchema,
  type Fill,
  type Layer,
  type TemplateDoc,
} from '../template';
import { isVerticalVideo, VERTICAL_TEMPLATE_MARGINS } from '../video-formats';

/**
 * The AI director's hands: tools a model calls to compose a banner or a video from scratch —
 * scenes, texts with fonts and animation, shapes, photos, the client's logo, motion graphics,
 * text effects, transitions, camera moves and music — and to change or remove what it made.
 * Every call is validated and answered (ok, or an error the model can fix). Before it finishes,
 * the director reviews its own work like an art director would (texts that won't fit, overlap
 * or can't be read, scenes too short to read, repeated lines, no call to action) and fixes it.
 * The result is always a valid template document; with a brand kit, its colours, fonts and
 * logo stay linked to the brand.
 */

export type ComposeMode = 'banner' | 'video';

export interface ToolCall {
  name: string;
  args: Record<string, unknown>;
}

export interface ToolResult {
  ok: boolean;
  /** Id of what was created (scene or layer), to refer to it in later calls. */
  id?: string;
  error?: string;
  /** Short human summary of the step. */
  summary: string;
  /** Findings for the model (the review's list of problems). */
  notes?: string;
}

export interface ComposeResult {
  doc: TemplateDoc;
  name: string;
  music: { style: MusicStyle; energy: MusicEnergy } | null;
}

// ---------------------------------------------------------------- catalog (from code)

const FONT_FAMILIES = BUILTIN_FONTS.map((f) => f.family);
const CYRILLIC = new Set(BUILTIN_FONTS.filter((f) => f.cyrillic).map((f) => f.family));
const PHOTO_IDS = Object.keys(PHOTOS);
const FX_IDS = FX_KINDS.map((k) => k.id);
const ENTER_IDS = ENTER_PRESETS.map((p) => p.id);
const EMPHASIS_IDS = EMPHASIS_PRESETS.map((p) => p.id);
const TRANSITION_IDS = TRANSITIONS.map((p) => p.id);
const CAMERA_IDS = CAMERA_PRESETS.map((p) => p.id);
const TEXT_ROLES = [
  'headline',
  'subtitle',
  'kicker',
  'cta',
  'price',
  'label',
  'body',
  'quote',
] as const;
const SHAPE_IDS = SHAPES.filter((s) => s !== 'icon');

// Seconds and minutes in the editor's languages. Single letters only stand alone ("15 с", "15s"),
// so "3 сцени" or "3 scenes" is not read as a length.
const SECONDS_RE =
  /(\d+(?:[.,]\d+)?)\s*-?\s*(?:сек(?:унд\p{L}*)?|с|s|secs?|seconds?|sekund\p{L}*|segund\p{L}*|secund\p{L}*)(?!\p{L})/iu;
const MINUTES_RE =
  /(\d+(?:[.,]\d+)?)\s*-?\s*(?:хв(?:илин\p{L}*)?|мин(?:ут\p{L}*)?|min(?:ut\p{L}*|s)?)(?!\p{L})/iu;
const HALF_MINUTE_RE = /пів\s*хвилин|пол\s*минут|half\s+a\s+minute|pół\s+minuty/iu;
export const VIDEO_TARGET_MIN_MS = 5_000;
export const VIDEO_TARGET_MAX_MS = 60_000;

/**
 * The video length asked for in the brief ("ролик на 15 секунд", "20s", "1 хвилина", "пів хвилини"),
 * in ms, or null. Lengths over two minutes are about the subject ("90 хвилин матчу"), not the video.
 */
export function briefDurationMs(brief: string): number | null {
  if (HALF_MINUTE_RE.test(brief)) return 30_000;
  const read = (re: RegExp, unit: number) => {
    const m = re.exec(brief);
    return m ? Number(m[1].replace(',', '.')) * unit : null;
  };
  const ms = read(SECONDS_RE, 1000) ?? read(MINUTES_RE, 60_000);
  if (ms === null || ms <= 0 || ms > 120_000) return null;
  return clamp(Math.round(ms), VIDEO_TARGET_MIN_MS, VIDEO_TARGET_MAX_MS);
}

/** How many scenes a video of this length gets (about 3 s each). */
const scenesFor = (ms: number) => clamp(Math.round(ms / 3000), 2, 12);

function paramDoc(kind: FxKind): string {
  return kind.params
    .filter((p) => p.type !== 'layer' && p.key !== 'source' && p.key !== 'hideSource')
    .map((p) => {
      const range =
        p.type === 'select'
          ? `one of ${p.options?.map((o) => o.value).join('|')}`
          : p.type === 'number'
            ? `${p.min ?? ''}..${p.max ?? ''}`
            : p.type === 'color'
              ? 'colour #hex or accent|accent2|dark|light|ink'
              : p.type;
      return `${p.key} (${range}, default ${JSON.stringify(p.default)})`;
    })
    .join('; ');
}

/** Static system instructions (a cacheable prefix), generated from the code's catalogs. */
export function composerSystemPrompt(): string {
  const fx = FX_KINDS.filter((k) => !['captions'].includes(k.id));
  return [
    'You are an award-winning motion designer, art director and copywriter working inside a banner and video',
    'editor. You compose the piece yourself by calling tools: plan the canvas, add scenes, then',
    'texts, shapes, photos, motion graphics, text effects, transitions, camera moves and music.',
    'Every tool answers ok or an error: fix errors and continue. Call finish when done.',
    '',
    'Workflow:',
    '1. plan_style: the idea, every on-screen line scene by scene, palette and fonts.',
    '2. Build scene by scene (add_scene, then its texts, shapes, photos, logo, graphics).',
    '3. review: an art director checks the piece (texts that will not fit their box, overlap or',
    '   cannot be read on their background, scenes too short to read, repeated lines, no call to',
    '   action). Fix every point with update / remove / update_scene, using the ids it gives.',
    '4. finish. finish also runs the review and refuses once while problems remain.',
    'Tools return ids (s1… for scenes, layer ids, fx-… for graphics): keep them to fix things later.',
    '',
    'Craft rules:',
    '- Start with plan_style (the concept, palette and fonts), then build scene by scene.',
    '- Work in batches: every answer makes several tool calls at once, e.g. a scene with all its texts,',
    '  shapes and graphics in one answer. One call per answer is too slow: the piece is never finished.',
    '- Video: 4-8 scenes of 2-4 s, each with one clear idea: hook → value → proof/details → call to action.',
    '  When a length is asked, the scene durations must add up to it: never finish a shorter video.',
    '  Banner: exactly one scene.',
    '- Boxes are in percent of the safe frame (x, y, w, h from 0 to 100). Keep texts inside and apart.',
    '- Big confident type: headlines 8-20 (size in % of the short side), subtitles 3.5-6, labels 2-3.',
    '- Contrast: light text on dark backgrounds and the opposite. Use the palette accents for highlights.',
    '- Short copy: headlines 2-6 words, never long sentences. Write in the requested language.',
    '',
    'Copywriting (you write every word on screen, the client does not):',
    '- The brief is the client talking to you: what to promote, the facts, the mood. It is not copy.',
    '  Find the idea behind it and write fresh lines a top agency would put on screen.',
    '- Words that describe the piece or its mood ("epic", "cool", "tell about", "show how awesome") are',
    '  directions: make the viewer feel them through strong verbs, images, contrast and rhythm. Never put',
    "  them on screen, and never paste the brief's phrases word for word.",
    '- Keep exactly: product, brand, title and team names, offers, facts. The wording around them is yours.',
    '- Tell a story across the scenes: a hook that grabs (a question, a challenge, a bold claim), then',
    '  build up, then the payoff and the call to action. Every scene says something new; never repeat a',
    '  line or say the same thing twice in other words.',
    '- Add the copy the brief does not spell out: kickers, subtitles, short punchy lines that fit the idea.',
    '  Scenes may have a headline with a supporting line, not only one word.',
    '- Native, idiomatic language: wordplay and rhythm welcome, no literal translations, no clichés like',
    '  "don\'t miss it" when a sharper line fits.',
    '- Example. Brief: "A video about our cool new crime series, make it epic, only on Kino+".',
    '  Bad (the brief pasted): "COOL NEW SERIES" / "EPIC CRIME" / "ONLY ON KINO+".',
    '  Good (written): "Everyone has a price" / "Not everyone pays it" / "The new crime series" /',
    '  "Season 1 · only on Kino+". The epic mood is in the words and the pacing, never in "epic".',
    '- Animate every text (enter preset, often a split by word or char) with delays so things arrive',
    '  one after another; add an emphasis on the call to action. Mix in motion graphics (2-6 per video)',
    '  and text effects for the boldest words; vary scene transitions; a camera move or two.',
    '- Facts only from the brief: never invent prices, dates, numbers, names, results or quotes.',
    '- Layout: a clear hierarchy per scene (one dominant element), generous margins, aligned edges;',
    '  give every text a box big enough for it (a 5-word headline at size 12 needs about w 90, h 30).',
    '- The piece becomes a template filled from data later: one headline per scene, and the roles',
    '  (headline, subtitle, price, cta…) name the layers, so use them for what they are.',
    "- A brand kit, when given, is the client's identity: use its colours, fonts and logo.",
    '',
    `Fonts (Cyrillic-capable marked *): ${BUILTIN_FONTS.map((f) => f.family + (f.cyrillic ? '*' : '')).join(', ')}.`,
    `Photos: ${Object.entries(PHOTOS)
      .map(([id, p]) => `${id} (${p.alt})`)
      .join('; ')}.`,
    `Enter presets (also used for exits): ${ENTER_IDS.join(', ')}.`,
    `Emphasis presets: ${EMPHASIS_IDS.join(', ')}.`,
    `Scene transitions: ${TRANSITION_IDS.join(', ')}.`,
    `Camera moves: ${CAMERA_IDS.join(', ')}.`,
    `Shapes: ${SHAPE_IDS.join(', ')}.`,
    `Music styles: ${MUSIC_STYLES.join(', ')}; energy: ${MUSIC_ENERGIES.join(', ')}.`,
    '',
    `Text effects (effect_kind of add_text; the text is drawn with its own font): ${[...TEXT_EFFECT_KINDS].join(', ')}.`,
    'Motion graphics (add_graphic kind; params as key/value pairs):',
    ...fx.map((k) => `- ${k.id} [${k.category}]: ${k.label[0]}. ${paramDoc(k)}`),
  ].join('\n');
}

export function composerUserPrompt(input: {
  brief: string;
  mode: ComposeMode;
  width: number;
  height: number;
  locale: LocaleId;
  durationMs?: number;
  brand?: BrandKit | null;
}): string {
  const target = input.mode === 'video' ? (input.durationMs ?? briefDurationMs(input.brief)) : null;
  const LANG: Record<LocaleId, string> = {
    en: 'English',
    uk: 'Ukrainian',
    pl: 'Polish',
    ro: 'Romanian',
    es: 'Spanish',
    ru: 'Russian',
  };
  const n = target ? scenesFor(target) : 0;
  return [
    `Make a ${input.mode === 'video' ? 'short video' : 'static banner'} of ${input.width}x${input.height} px.`,
    ...(input.mode !== 'video'
      ? []
      : target
        ? [
            `Length: exactly ${target / 1000} s (the sum of the scene durations): about ${n} scenes of ${(target / n / 1000).toFixed(1)} s each.`,
          ]
        : ['Length: 10-15 s, 4-6 scenes.']),
    `Language of all texts: ${LANG[input.locale]}${['uk', 'ru'].includes(input.locale) ? ' (use Cyrillic-capable fonts)' : ''}.`,
    ...brandLines(input.brand),
    '',
    'Brief from the client (their words about what they want, not the copy):',
    input.brief,
  ].join('\n');
}

/** The client's brand kit for the prompt. */
function brandLines(kit: BrandKit | null | undefined): string[] {
  if (!kit) return [];
  const colors = BRAND_COLOR_SLOTS.filter((c) => kit.colors[c]).map((c) => `${c} ${kit.colors[c]}`);
  const heading = kit.fonts['font-heading']?.family;
  const body = kit.fonts['font-body']?.family;
  const logos = Object.keys(kit.logos).filter((k) => kit.logos[k as keyof typeof kit.logos]);
  if (!colors.length && !heading && !body && !logos.length) return [];
  return [
    'Brand kit of the client (use it):',
    ...(colors.length ? [`- colours: ${colors.join(', ')} (build the palette from these)`] : []),
    ...(heading || body
      ? [`- fonts: headings ${heading ?? 'any'}, texts ${body ?? heading ?? 'any'}`]
      : []),
    ...(logos.length
      ? [`- logo: add_logo (variants ${logos.join(', ')}); show it at least in the last scene`]
      : []),
  ];
}

// ---------------------------------------------------------------- tool declarations (Gemini)

const S = (description?: string, extra: Record<string, unknown> = {}) => ({
  type: 'STRING',
  ...(description ? { description } : {}),
  ...extra,
});
const N = (description?: string, extra: Record<string, unknown> = {}) => ({
  type: 'NUMBER',
  ...(description ? { description } : {}),
  ...extra,
});
const E = (values: readonly string[], description?: string) =>
  S(description, { enum: [...values] });
const BOX = {
  type: 'OBJECT',
  description: 'Percent of the safe frame',
  properties: { x: N(), y: N(), w: N(), h: N() },
  required: ['x', 'y', 'w', 'h'],
};
const PARAMS = {
  type: 'ARRAY',
  description: 'Parameters as key/value pairs (values as text)',
  items: { type: 'OBJECT', properties: { key: S(), value: S() }, required: ['key', 'value'] },
};

/** Function declarations for the model, enums generated from the catalogs. */
export function composerTools(mode: ComposeMode, opts: { logo?: boolean } = {}) {
  const video = mode === 'video';
  const anim = video
    ? {
        enter: E(ENTER_IDS, 'How it appears'),
        exit: E(ENTER_IDS, 'How it leaves (optional)'),
        emphasis: E(EMPHASIS_IDS, 'Attention move after it appears (optional)'),
        delay_ms: N('Delay after the scene starts'),
      }
    : {};
  return [
    {
      name: 'plan_style',
      description: 'Concept, palette and fonts of the piece (call first).',
      parameters: {
        type: 'OBJECT',
        properties: {
          concept: S(
            "Not shown: the big idea, then every on-screen line scene by scene, in the language of the texts, written by you (not the brief's words)",
          ),
          background: S('#hex'),
          text: S('#hex main text colour'),
          accent: S('#hex'),
          accent2: S('#hex'),
          // Free text, not an enum: Gemini rejects a schema with all the font names (too many
          // states). The list is in the system prompt; unknown names fall back to the default.
          display_font: S('A font from the list'),
          body_font: S('A font from the list'),
        },
        required: ['concept', 'background', 'text', 'accent', 'display_font', 'body_font'],
      },
    },
    {
      name: 'add_scene',
      description: video
        ? 'A scene (shown after the previous one). Its id is s1, s2, s3… in the order added.'
        : 'The banner (call once). Returns its id.',
      parameters: {
        type: 'OBJECT',
        properties: {
          name: S(),
          ...(video
            ? { duration_ms: N('1500-6000'), transition: E(TRANSITION_IDS, 'Into the next scene') }
            : {}),
          background: S('#hex, or two #hex for a gradient separated by a space, or a photo id'),
        },
        required: ['name', 'background'],
      },
    },
    {
      name: 'add_text',
      description: 'A text in a scene.',
      parameters: {
        type: 'OBJECT',
        properties: {
          scene: S('Scene id (s1, s2…) or name'),
          role: E(TEXT_ROLES),
          text: S(),
          box: BOX,
          size: N('Font size, % of the short side (2-25)'),
          weight: N('100-900'),
          font: S('A font from the list. Default: display font for headlines, body font otherwise'),
          color: S('#hex'),
          align: E(['left', 'center', 'right']),
          uppercase: { type: 'BOOLEAN' },
          pill: S('#hex: draws the text on a rounded pill (buttons, badges)'),
          ...anim,
          ...(video
            ? {
                split: E(SPLIT_UNITS, 'Animate lines, words or letters one after another'),
                effect_kind: E([...TEXT_EFFECT_KINDS], 'Text effect drawn with this text and font'),
                effect_params: PARAMS,
              }
            : {}),
        },
        required: ['scene', 'role', 'text', 'box', 'size'],
      },
    },
    {
      name: 'add_shape',
      description: 'A shape (panel, badge, line, decoration) in a scene.',
      parameters: {
        type: 'OBJECT',
        properties: {
          scene: S(),
          shape: E(SHAPE_IDS),
          box: BOX,
          color: S('#hex, or two #hex for a gradient'),
          radius: N('Corner radius, % of the short side'),
          opacity: N('0-1'),
          ...anim,
        },
        required: ['scene', 'shape', 'box', 'color'],
      },
    },
    {
      name: 'add_photo',
      description: 'A photo in a scene (use a full box for a background).',
      parameters: {
        type: 'OBJECT',
        properties: {
          scene: S(),
          photo: E(PHOTO_IDS),
          box: BOX,
          radius: N('Corner radius, % of the short side'),
          shade: N('Darken it for readable text, 0-0.8'),
          ...(video ? { ken_burns: { type: 'BOOLEAN' } } : {}),
          ...anim,
        },
        required: ['scene', 'photo', 'box'],
      },
    },
    ...(opts.logo
      ? [
          {
            name: 'add_logo',
            description: "The client's logo from the brand kit in a scene.",
            parameters: {
              type: 'OBJECT',
              properties: {
                scene: S(),
                box: BOX,
                variant: E(['logo', 'logo-light', 'logo-icon'], 'logo-light on dark backgrounds'),
                ...anim,
              },
              required: ['scene', 'box'],
            },
          },
        ]
      : []),
    ...(video
      ? [
          {
            name: 'add_graphic',
            description:
              'Motion graphics (see the catalog) in a scene, or over the whole video without a scene.',
            parameters: {
              type: 'OBJECT',
              properties: {
                kind: E(FX_IDS),
                scene: S('Scene id (optional)'),
                at_ms: N('Start after the scene starts'),
                duration_ms: N(),
                behind: { type: 'BOOLEAN', description: 'Under the layers instead of over them' },
                params: PARAMS,
              },
              required: ['kind'],
            },
          },
          {
            name: 'add_camera',
            description: 'A camera move of the whole picture in a scene.',
            parameters: {
              type: 'OBJECT',
              properties: { scene: S(), preset: E(CAMERA_IDS), at_ms: N(), duration_ms: N() },
              required: ['scene', 'preset'],
            },
          },
          {
            name: 'set_music',
            description: 'Music generated for the video.',
            parameters: {
              type: 'OBJECT',
              properties: { style: E(MUSIC_STYLES), energy: E(MUSIC_ENERGIES) },
              required: ['style'],
            },
          },
        ]
      : []),
    {
      name: 'update',
      description:
        'Change something already made (a layer or a graphic, by its id): only the fields given change.',
      parameters: {
        type: 'OBJECT',
        properties: {
          id: S('Layer id or graphic id (fx-…)'),
          text: S(),
          box: BOX,
          size: N('Font size, % of the short side'),
          weight: N(),
          font: S(),
          color: S('#hex (text colour, shape fill)'),
          align: E(['left', 'center', 'right']),
          uppercase: { type: 'BOOLEAN' },
          opacity: N('0-1'),
          ...anim,
          ...(video
            ? {
                at_ms: N('Graphics: start after the scene starts'),
                duration_ms: N('Graphics: length'),
                params: PARAMS,
              }
            : {}),
        },
        required: ['id'],
      },
    },
    {
      name: 'remove',
      description: 'Remove a layer or a graphic by its id.',
      parameters: {
        type: 'OBJECT',
        properties: { id: S() },
        required: ['id'],
      },
    },
    {
      name: 'update_scene',
      description:
        'Change a scene: name, background colour' + (video ? ', length, transition' : ''),
      parameters: {
        type: 'OBJECT',
        properties: {
          scene: S(),
          name: S(),
          background: S('#hex, or two #hex for a gradient'),
          ...(video
            ? { duration_ms: N('1500-6000'), transition: E(TRANSITION_IDS, 'Into the next scene') }
            : {}),
        },
        required: ['scene'],
      },
    },
    {
      name: 'review',
      description:
        'Art-direction check of the piece so far: every problem with the id to fix (call before finish).',
      parameters: { type: 'OBJECT', properties: {} },
    },
    {
      name: 'finish',
      description: 'Done: name the piece.',
      parameters: {
        type: 'OBJECT',
        properties: {
          name: S('Short name'),
          ...(video
            ? {
                sound_effects: {
                  type: 'BOOLEAN',
                  description: 'Whooshes, hits and ticks on the cuts',
                },
              }
            : {}),
        },
        required: ['name'],
      },
    },
  ];
}

// ---------------------------------------------------------------- the executor

interface SceneDraft {
  id: string;
  name: string;
  duration: number;
  transition: string | null;
  background: Fill | { photo: string };
  /** The layer drawing the background colour (photo backgrounds have none). */
  bgLayerId?: string;
}

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
const num = (v: unknown, d: number) =>
  typeof v === 'number' && Number.isFinite(v)
    ? v
    : typeof v === 'string' && v.trim() && Number.isFinite(Number(v))
      ? Number(v)
      : d;
const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
const color = (v: unknown, d: string) => {
  const s = str(v);
  return HEX_COLOR_RE.test(s) ? s : d;
};

function fillOf(v: unknown, d: string): Fill {
  const parts = str(v)
    .split(/[\s,]+/)
    .filter((x) => HEX_COLOR_RE.test(x));
  if (parts.length >= 2)
    return {
      type: 'linear',
      angle: 160,
      stops: parts.slice(0, 4).map((c, i, a) => ({ offset: i / (a.length - 1), color: c })),
    };
  return { type: 'solid', color: parts[0] ?? d };
}

// ---------------------------------------------------------------- review helpers

const hexRgb = (hex: string): [number, number, number] => {
  let h = hex.replace('#', '');
  if (h.length <= 4) h = [...h].map((c) => c + c).join('');
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) || 0) as [number, number, number];
};
const hexAlpha = (hex: string) => {
  const h = hex.replace('#', '');
  if (h.length === 4) return parseInt(h[3] + h[3], 16) / 255;
  if (h.length === 8) return parseInt(h.slice(6, 8), 16) / 255;
  return 1;
};
const rgbHex = (c: number[]) =>
  `#${c
    .map((v) =>
      Math.round(clamp(v, 0, 255))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
const blend = (under: number[], over: number[], a: number) =>
  under.map((u, i) => u * (1 - a) + over[i] * a) as [number, number, number];

/** One colour standing for a fill (a gradient: its middle). */
function fillColor(f: Fill | { photo: string }, d: string): string {
  if ('photo' in f) return '#808080';
  if (f.type === 'solid') return f.color.startsWith('brand:') ? f.color.split('|')[1] : f.color;
  const stops = 'stops' in f ? f.stops : [];
  if (!stops.length) return d;
  const sum = [0, 0, 0];
  let alpha = 0;
  for (const st of stops) {
    hexRgb(st.color).forEach((v, k) => (sum[k] += v / stops.length));
    alpha += hexAlpha(st.color) / stops.length;
  }
  // Translucent gradients (photo shades) keep their average transparency
  return (
    rgbHex(sum) +
    Math.round(alpha * 255)
      .toString(16)
      .padStart(2, '0')
  );
}

/** WCAG contrast ratio of two colours. */
export function contrastRatio(a: string, b: string): number {
  const lum = (hex: string) => {
    const [r, g, bl] = hexRgb(hex).map((v) => {
      const c = v / 255;
      return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * bl;
  };
  const [x, y] = [lum(a), lum(b)].sort((p, q) => q - p);
  return (x + 0.05) / (y + 0.05);
}

/**
 * How much auto-fit would shrink a text to fit its box (1: fits as is), from average glyph
 * widths: a lenient estimate, no font metrics.
 */
export function fitScale(
  t: {
    text: string;
    fontSize: number;
    fontWeight: number;
    lineHeight: number;
    letterSpacing: number;
    textTransform: string;
  },
  width: number,
  height: number,
): number {
  const glyph = (t.textTransform === 'uppercase' ? 0.62 : 0.52) * (t.fontWeight >= 700 ? 1.05 : 1);
  const lines = t.text.split('\n');
  const fits = (size: number) => {
    const char = size * glyph + t.letterSpacing;
    const perLine = Math.max(1, Math.floor(width / Math.max(1, char)));
    let rows = 0;
    for (const line of lines) {
      const words = line.split(/\s+/).filter(Boolean);
      if (words.some((w) => w.length > perLine)) return false;
      let row = 0;
      rows++;
      for (const w of words) {
        if (row && row + 1 + w.length > perLine) {
          rows++;
          row = w.length;
        } else row += (row ? 1 : 0) + w.length;
      }
    }
    return rows * size * t.lineHeight <= height * 1.02;
  };
  let size = t.fontSize;
  while (size > t.fontSize * 0.2 && !fits(size)) size *= 0.95;
  return size / t.fontSize;
}

export class Composer {
  readonly W: number;
  readonly H: number;
  readonly mode: ComposeMode;
  readonly locale: LocaleId;
  private F: { x: number; y: number; w: number; h: number };
  private scenes: SceneDraft[] = [];
  private layers: Layer[] = [];
  private fx: Array<DocMotion['fx'][number] & { sceneId?: string; at: number }> = [];
  private camera: Array<{
    sceneId: string;
    preset: string;
    at: number;
    duration: number;
    id: string;
  }> = [];
  private names = new Set<string>();
  private style = {
    background: '#0E0E12',
    text: '#FFFFFF',
    accent: '#FF4A0D',
    accent2: '#2B3BFF',
    display: 'Inter',
    body: 'Inter',
  };
  private music: ComposeResult['music'] = null;
  private sound = true;
  name = '';
  finished = false;
  /** Length of the video asked for (explicitly or in the brief), ms. */
  readonly targetMs: number | null;
  private lengthChecked = false;
  private reviewed = false;
  /** The client's brand kit: its colours, fonts and logo become brand references. */
  readonly brand: BrandKit | null;

  constructor(opts: {
    width: number;
    height: number;
    mode: ComposeMode;
    locale: LocaleId;
    brief?: string;
    durationMs?: number;
    brand?: BrandKit | null;
  }) {
    this.brand = opts.brand ?? null;
    this.W = opts.width;
    this.H = opts.height;
    this.mode = opts.mode;
    this.locale = opts.locale;
    this.targetMs =
      opts.mode === 'video'
        ? opts.durationMs
          ? clamp(opts.durationMs, VIDEO_TARGET_MIN_MS, VIDEO_TARGET_MAX_MS)
          : opts.brief
            ? briefDurationMs(opts.brief)
            : null
        : null;
    const z = isVerticalVideo(this.W, this.H)
      ? VERTICAL_TEMPLATE_MARGINS
      : { top: 0.06, bottom: 0.06, left: 0.06, right: 0.06 };
    this.F = {
      x: Math.ceil(z.left * this.W),
      y: Math.ceil(z.top * this.H),
      w: Math.floor(this.W * (1 - z.left - z.right)),
      h: Math.floor(this.H * (1 - z.top - z.bottom)),
    };
    // The brand's identity is the starting style
    const k = this.brand;
    if (k) {
      const c = k.colors;
      this.style = {
        background: c.background ?? this.style.background,
        text: c.text ?? this.style.text,
        accent: c.primary ?? c.accent ?? this.style.accent,
        accent2: c.secondary ?? c.accent ?? this.style.accent2,
        display: k.fonts['font-heading']?.family ?? this.style.display,
        body: k.fonts['font-body']?.family ?? k.fonts['font-heading']?.family ?? this.style.body,
      };
    }
  }

  /** Has the brand kit a logo for the director to place? */
  get hasLogo(): boolean {
    return Boolean(this.brand && Object.values(this.brand.logos).some(Boolean));
  }

  private get u() {
    return Math.min(this.W, this.H);
  }

  private box(v: unknown) {
    const b = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
    const x = clamp(num(b.x, 0), 0, 100);
    const y = clamp(num(b.y, 0), 0, 100);
    const w = clamp(num(b.w, 100), 2, 100 - x);
    const h = clamp(num(b.h, 20), 1, 100 - y);
    // A full box (0,0,100,100) of a shape or photo means the whole canvas, edge to edge
    const fullBleed = x <= 0.5 && y <= 0.5 && w >= 99.5 && h >= 99.5;
    return fullBleed
      ? { x: 0, y: 0, w: this.W, h: this.H, fullBleed }
      : {
          x: this.F.x + (this.F.w * x) / 100,
          y: this.F.y + (this.F.h * y) / 100,
          w: (this.F.w * w) / 100,
          h: (this.F.h * h) / 100,
          fullBleed,
        };
  }

  private scene(id: unknown): SceneDraft | undefined {
    const s = str(id);
    // In a batch the model does not know the new scene's id yet and names it instead
    const named = s.toLowerCase();
    return (
      this.scenes.find((x) => x.id === s) ??
      this.scenes.find((x) => x.name.toLowerCase() === named) ??
      (this.scenes.length === 1 || !s ? this.scenes[this.scenes.length - 1] : undefined)
    );
  }

  private font(v: unknown, d: string): string {
    const f = str(v);
    // The brand's own fonts (uploaded to the workspace) are the client's choice: kept as is
    const brandFonts = [
      this.brand?.fonts['font-heading']?.family,
      this.brand?.fonts['font-body']?.family,
    ];
    if (f && brandFonts.includes(f)) return f;
    const family = FONT_FAMILIES.includes(f) || brandFonts.includes(f) ? f : d;
    if (brandFonts.includes(family)) return family;
    if ((this.locale === 'uk' || this.locale === 'ru') && !CYRILLIC.has(family))
      return CYRILLIC.has(d) ? d : 'Inter';
    return family;
  }

  private name_(base: string) {
    const n = uniqueLayerName(
      base.replace(/[^a-z0-9_]/gi, '_').toLowerCase() || 'layer',
      this.names,
    );
    this.names.add(n);
    return n;
  }

  /** Motion of a layer from the common animation arguments. */
  private motion(a: Record<string, unknown>, scene: SceneDraft): LayerMotion {
    const m: LayerMotion = { scene: scene.id };
    if (this.mode !== 'video') return {};
    const D = scene.duration;
    const delay = clamp(num(a.delay_ms, 0), 0, D * 0.6);
    const enter = str(a.enter);
    if (ENTER_PRESET_MAP.has(enter)) {
      const p = ENTER_PRESET_MAP.get(enter)!;
      m.enter = { preset: enter, delay, duration: clamp(p.duration, 200, D * 0.5) };
      if (p.split && !a.split)
        m.split = {
          by: p.split.by ?? 'none',
          stagger: p.split.stagger ?? 40,
          order: p.split.order ?? 'start',
        };
    }
    const exit = str(a.exit);
    if (ENTER_PRESET_MAP.has(exit))
      m.exit = {
        preset: exit,
        delay: 0,
        duration: clamp(ENTER_PRESET_MAP.get(exit)!.duration, 200, D * 0.3),
      };
    const emph = str(a.emphasis);
    if (EMPHASIS_PRESET_MAP.has(emph)) {
      const p = EMPHASIS_PRESET_MAP.get(emph)!;
      const start = Math.min(D - p.duration, (m.enter ? delay + m.enter.duration : delay) + 200);
      if (start > 0)
        m.emphasis = {
          preset: emph,
          delay: start,
          duration: p.duration,
          repeat: p.ambient ? 1 : Math.max(1, p.repeat),
          gap: 200,
        };
    }
    const split = str(a.split);
    if ((SPLIT_UNITS as readonly string[]).includes(split) && split !== 'none')
      m.split = {
        by: split as MotionSplit['by'],
        stagger: split === 'char' ? 35 : split === 'word' ? 90 : 140,
        order: 'start',
      };
    return m;
  }

  private params(kind: FxKind, list: unknown): Record<string, string | number> {
    const out: Record<string, string | number> = {};
    const items = Array.isArray(list) ? list : [];
    for (const it of items) {
      if (!it || typeof it !== 'object') continue;
      const { key, value } = it as { key?: unknown; value?: unknown };
      const def = kind.params.find((p) => p.key === key);
      if (!def || def.type === 'layer') continue;
      if (def.type === 'number') {
        const v = num(value, NaN);
        if (Number.isFinite(v)) out[def.key] = clamp(v, def.min ?? -1e6, def.max ?? 1e6);
      } else if (def.type === 'select') {
        if (def.options?.some((o) => o.value === String(value))) out[def.key] = String(value);
      } else out[def.key] = String(value ?? '').slice(0, 1000);
    }
    return out;
  }

  /** Run one tool call. */
  call(c: ToolCall): ToolResult {
    try {
      return this.run(c);
    } catch (err) {
      return { ok: false, error: (err as Error).message, summary: `${c.name}: error` };
    }
  }

  private run({ name, args: a }: ToolCall): ToolResult {
    const video = this.mode === 'video';
    switch (name) {
      case 'plan_style': {
        this.style = {
          background: color(a.background, this.style.background),
          text: color(a.text, this.style.text),
          accent: color(a.accent, this.style.accent),
          accent2: color(a.accent2, this.style.accent2),
          display: this.font(a.display_font, 'Inter'),
          body: this.font(a.body_font, 'Inter'),
        };
        return {
          ok: true,
          summary: `${this.style.display} / ${this.style.body} · ${this.style.accent}`,
        };
      }
      case 'add_scene': {
        if (!video && this.scenes.length)
          return {
            ok: false,
            error: 'A banner has one scene: add the rest to it',
            summary: 'scene: refused',
          };
        if (this.scenes.length >= 12)
          return { ok: false, error: 'Too many scenes', summary: 'scene: refused' };
        const id = `s${this.scenes.length + 1}`;
        const bgRaw = str(a.background);
        const background = PHOTO_IDS.includes(bgRaw)
          ? { photo: bgRaw }
          : fillOf(bgRaw, this.style.background);
        const t = str(a.transition);
        this.scenes.push({
          id,
          name: str(a.name).slice(0, 64) || `Scene ${this.scenes.length + 1}`,
          duration: video ? clamp(num(a.duration_ms, 2500), 1200, 8000) : 3000,
          transition: TRANSITION_MAP.has(t) ? t : video ? 'crossfade' : null,
          background,
        });
        // The background is a layer of the scene (photos get a shade for readable text)
        const scene = this.scenes[this.scenes.length - 1];
        if ('photo' in background)
          this.addPhotoLayer(
            scene,
            background.photo,
            { x: 0, y: 0, w: this.W, h: this.H },
            0,
            0.35,
            video,
            {},
          );
        else {
          const bg = createLayer('shape', {
            name: this.name_(`${id}_bg`),
            shape: 'rectangle',
            fill: background,
            x: 0,
            y: 0,
            width: this.W,
            height: this.H,
            motion: video ? { scene: id } : undefined,
          } as Partial<Layer>);
          scene.bgLayerId = bg.id;
          this.push(bg);
        }
        return {
          ok: true,
          id,
          summary: `${scene.name}${video ? ` · ${(scene.duration / 1000).toFixed(1)} s` : ''}`,
        };
      }
      case 'add_text': {
        const scene = this.scene(a.scene);
        if (!scene)
          return {
            ok: false,
            error: `Unknown scene "${str(a.scene)}": add_scene first`,
            summary: 'text: no scene',
          };
        const text = str(a.text).slice(0, 300);
        if (!text) return { ok: false, error: 'Empty text', summary: 'text: empty' };
        const role = (TEXT_ROLES as readonly string[]).includes(str(a.role))
          ? str(a.role)
          : 'label';
        const b = this.box(a.box);
        const display = role === 'headline' || role === 'price' || role === 'quote';
        const size = Math.round((this.u * clamp(num(a.size, display ? 12 : 4), 1.5, 30)) / 100);
        const pill = HEX_COLOR_RE.test(str(a.pill)) ? str(a.pill) : null;
        const m = this.motion(a, scene);
        const kind = str(a.effect_kind);
        if (video && TEXT_EFFECT_KINDS.has(kind)) {
          const def = FX_KIND_MAP.get(kind)!;
          m.effect = {
            kind,
            delay: num(a.delay_ms, 0),
            duration: null,
            params: this.params(def, a.effect_params),
          };
        }
        const layer = createLayer('text', {
          name: this.name_(role),
          text,
          fontFamily: this.font(a.font, display ? this.style.display : this.style.body),
          fontWeight: clamp(Math.round(num(a.weight, display ? 800 : 500) / 100) * 100, 100, 900),
          fontSize: size,
          color: color(a.color, this.style.text),
          align: (['left', 'center', 'right'] as const).find((x) => x === str(a.align)) ?? 'center',
          verticalAlign: 'middle',
          lineHeight: display ? 1 : 1.25,
          letterSpacing: display ? -Math.round(size * 0.02) : 0,
          textTransform: a.uppercase === true ? 'uppercase' : 'none',
          autoFit: true,
          minFontSize: Math.max(8, Math.round(size * 0.35)),
          ...(pill
            ? {
                background: { type: 'solid', color: pill },
                borderRadius: Math.round(b.h / 2),
                padding: {
                  top: 0,
                  bottom: 0,
                  left: Math.round(size * 0.9),
                  right: Math.round(size * 0.9),
                },
                maxLines: 1,
              }
            : {}),
          x: Math.round(b.x),
          y: Math.round(b.y),
          width: Math.max(10, Math.round(b.w)),
          height: Math.max(10, Math.round(b.h)),
          motion: video ? m : undefined,
        } as Partial<Layer>);
        this.push(layer);
        return {
          ok: true,
          id: layer.id,
          summary: `${role}: «${text.slice(0, 40)}»${m.effect ? ` · ${m.effect.kind}` : m.enter ? ` · ${m.enter.preset}` : ''}`,
        };
      }
      case 'add_shape': {
        const scene = this.scene(a.scene);
        if (!scene) return { ok: false, error: 'Unknown scene', summary: 'shape: no scene' };
        const shape = (SHAPE_IDS as readonly string[]).includes(str(a.shape))
          ? str(a.shape)
          : 'rectangle';
        const b = this.box(a.box);
        const layer = createLayer('shape', {
          name: this.name_(shape),
          shape,
          fill: fillOf(a.color, this.style.accent),
          borderRadius: Math.round((this.u * clamp(num(a.radius, 0), 0, 50)) / 100),
          opacity: clamp(num(a.opacity, 1), 0.05, 1),
          x: Math.round(b.x),
          y: Math.round(b.y),
          width: Math.max(2, Math.round(b.w)),
          height: Math.max(2, Math.round(b.h)),
          motion: video ? this.motion(a, scene) : undefined,
        } as Partial<Layer>);
        this.push(layer);
        return { ok: true, id: layer.id, summary: shape };
      }
      case 'add_photo': {
        const scene = this.scene(a.scene);
        if (!scene) return { ok: false, error: 'Unknown scene', summary: 'photo: no scene' };
        const photo = str(a.photo);
        if (!PHOTO_IDS.includes(photo))
          return { ok: false, error: `Unknown photo "${photo}"`, summary: 'photo: unknown' };
        const b = this.box(a.box);
        const id = this.addPhotoLayer(
          scene,
          photo,
          b,
          num(a.radius, 0),
          clamp(num(a.shade, 0), 0, 0.85),
          video && a.ken_burns !== false,
          a,
        );
        return { ok: true, id, summary: photo };
      }
      case 'add_graphic': {
        if (!video)
          return { ok: false, error: 'Graphics need a video', summary: 'graphic: refused' };
        const kind = FX_KIND_MAP.get(str(a.kind));
        if (!kind)
          return {
            ok: false,
            error: `Unknown graphic "${str(a.kind)}"`,
            summary: 'graphic: unknown',
          };
        if (this.fx.length >= 30)
          return { ok: false, error: 'Too many graphics', summary: 'graphic: refused' };
        const scene = str(a.scene) ? this.scene(a.scene) : undefined;
        const at = Math.max(0, num(a.at_ms, 0));
        const duration = clamp(
          num(a.duration_ms, scene ? scene.duration : kind.duration),
          200,
          60_000,
        );
        const fxId = `fx-${this.fx.length + 1}-${kind.id}`;
        this.fx.push({
          id: fxId,
          kind: kind.id,
          start: 0,
          duration,
          z: a.behind === true ? 'back' : kind.z,
          params: this.params(kind, a.params),
          sceneId: scene?.id,
          at,
        });
        return {
          ok: true,
          id: fxId,
          summary: `${kind.label[0]}${scene ? ` · ${scene.name}` : ''}`,
        };
      }
      case 'add_camera': {
        const scene = this.scene(a.scene);
        const preset = str(a.preset);
        if (!scene || !CAMERA_PRESET_MAP.has(preset))
          return { ok: false, error: 'Unknown scene or camera preset', summary: 'camera: refused' };
        this.camera.push({
          id: `cam-${this.camera.length + 1}`,
          sceneId: scene.id,
          preset,
          at: Math.max(0, num(a.at_ms, 0)),
          duration: clamp(num(a.duration_ms, 1200), 200, 10_000),
        });
        return { ok: true, summary: preset };
      }
      case 'set_music': {
        const style = str(a.style) as MusicStyle;
        if (!(MUSIC_STYLES as readonly string[]).includes(style))
          return { ok: false, error: 'Unknown music style', summary: 'music: unknown' };
        const energy = (MUSIC_ENERGIES as readonly string[]).includes(str(a.energy))
          ? (str(a.energy) as MusicEnergy)
          : 'normal';
        this.music = { style, energy };
        return { ok: true, summary: `${style} · ${energy}` };
      }
      case 'add_logo': {
        const scene = this.scene(a.scene);
        if (!scene) return { ok: false, error: 'Unknown scene', summary: 'logo: no scene' };
        const logos = this.brand?.logos ?? {};
        const variant = (['logo', 'logo-light', 'logo-icon'] as const).find(
          (v) => v === str(a.variant) && logos[v],
        );
        const slot =
          variant ?? (['logo', 'logo-light', 'logo-icon'] as const).find((v) => logos[v]);
        if (!slot) return { ok: false, error: 'The brand kit has no logo', summary: 'logo: none' };
        const b = this.box(a.box);
        const layer = createLayer('image', {
          name: this.name_('logo'),
          // A brand reference: a new logo in the kit shows up in the template too
          src: `brand:${slot}|${logos[slot]}`,
          fit: 'contain',
          x: Math.round(b.x),
          y: Math.round(b.y),
          width: Math.max(8, Math.round(b.w)),
          height: Math.max(8, Math.round(b.h)),
          motion: video ? this.motion(a, scene) : undefined,
        } as Partial<Layer>);
        this.push(layer);
        return { ok: true, id: layer.id, summary: `logo · ${scene.name}` };
      }
      case 'update':
        return this.update(a);
      case 'remove': {
        const id = str(a.id);
        const before = this.layers.length + this.fx.length + this.camera.length;
        this.layers = this.layers.filter((l) => l.id !== id);
        this.fx = this.fx.filter((f) => f.id !== id);
        this.camera = this.camera.filter((c) => c.id !== id);
        if (before === this.layers.length + this.fx.length + this.camera.length)
          return { ok: false, error: `Nothing with id "${id}"`, summary: 'remove: not found' };
        return { ok: true, summary: id };
      }
      case 'update_scene': {
        const scene = this.scene(a.scene);
        if (!scene) return { ok: false, error: 'Unknown scene', summary: 'scene: not found' };
        if (str(a.name)) scene.name = str(a.name).slice(0, 64);
        if (video && a.duration_ms !== undefined)
          scene.duration = clamp(num(a.duration_ms, scene.duration), 1200, 8000);
        const t = str(a.transition);
        if (video && TRANSITION_MAP.has(t)) scene.transition = t;
        if (str(a.background)) {
          const fill = fillOf(a.background, this.style.background);
          const bg = this.layers.find((l) => l.id === scene.bgLayerId);
          if (!bg || bg.type !== 'shape')
            return {
              ok: false,
              error: 'This scene has a photo background: update or remove the photo instead',
              summary: 'scene: photo background',
            };
          bg.fill = fill;
          scene.background = fill;
        }
        return {
          ok: true,
          id: scene.id,
          summary: `${scene.name}${video ? ` · ${(scene.duration / 1000).toFixed(1)} s` : ''}`,
        };
      }
      case 'review': {
        const issues = this.review();
        return {
          ok: true,
          summary: issues.length ? `review: ${issues.length} issue(s)` : 'review: clean',
          notes: issues.length
            ? `${issues.map((i) => `- ${i}`).join('\n')}\nFix them with update / remove / update_scene.`
            : 'No problems found.',
        };
      }
      case 'finish': {
        if (!this.scenes.length)
          return {
            ok: false,
            error: 'Nothing to finish: add a scene first',
            summary: 'finish: empty',
          };
        this.name = str(a.name).slice(0, 80) || 'AI design';
        this.sound = a.sound_effects !== false;
        // Too short a video gets one refusal (the model adds scenes); build() fits the rest
        const total = this.totalMs();
        const want = this.targetMs ?? 8000;
        if (video && !this.lengthChecked && total < want * 0.85) {
          this.lengthChecked = true;
          const more = Math.max(1, Math.round((want - total) / 3000));
          return {
            ok: false,
            error: `The video is ${(total / 1000).toFixed(1)} s, it must be ${want / 1000} s: add about ${more} more scene(s) with their texts, then call finish again`,
            summary: `finish: ${(total / 1000).toFixed(1)} of ${want / 1000} s`,
          };
        }
        // An art director's look before it ships: one round of fixes
        if (!this.reviewed) {
          this.reviewed = true;
          const issues = this.review();
          if (issues.length)
            return {
              ok: false,
              error: [
                `Review found ${issues.length} problem(s):`,
                ...issues.map((i) => `- ${i}`),
                'Fix them with update / remove / update_scene (ids above), then call finish again.',
                'If a point is intended, call finish again as is.',
              ].join('\n'),
              summary: `review: ${issues.length} issue(s)`,
            };
        }
        this.finished = true;
        return { ok: true, summary: this.name };
      }
      default:
        return { ok: false, error: `Unknown tool ${name}`, summary: `${name}: unknown` };
    }
  }

  /** Change a layer or a graphic already made; only the fields given change. */
  private update(a: Record<string, unknown>): ToolResult {
    const id = str(a.id);
    const video = this.mode === 'video';
    const fx = this.fx.find((f) => f.id === id);
    if (fx) {
      const kind = FX_KIND_MAP.get(fx.kind)!;
      if (a.params !== undefined) fx.params = { ...fx.params, ...this.params(kind, a.params) };
      if (a.at_ms !== undefined) fx.at = Math.max(0, num(a.at_ms, fx.at));
      if (a.duration_ms !== undefined)
        fx.duration = clamp(num(a.duration_ms, fx.duration), 200, 60_000);
      return { ok: true, id, summary: `${kind.label[0]}: updated` };
    }
    const layer = this.layers.find((l) => l.id === id);
    if (!layer)
      return { ok: false, error: `Nothing with id "${id}"`, summary: 'update: not found' };
    if (a.box !== undefined) {
      const b = this.box(a.box);
      layer.x = Math.round(b.x);
      layer.y = Math.round(b.y);
      layer.width = Math.max(4, Math.round(b.w));
      layer.height = Math.max(4, Math.round(b.h));
    }
    if (a.opacity !== undefined) layer.opacity = clamp(num(a.opacity, layer.opacity), 0.05, 1);
    if (layer.type === 'text') {
      if (str(a.text)) layer.text = str(a.text).slice(0, 300);
      if (a.size !== undefined) {
        layer.fontSize = Math.round((this.u * clamp(num(a.size, 4), 1.5, 30)) / 100);
        layer.minFontSize = Math.max(8, Math.round(layer.fontSize * 0.35));
      }
      if (a.weight !== undefined)
        layer.fontWeight = clamp(Math.round(num(a.weight, 400) / 100) * 100, 100, 900);
      if (str(a.font)) layer.fontFamily = this.font(a.font, layer.fontFamily);
      if (str(a.color)) layer.color = color(a.color, layer.color);
      const align = (['left', 'center', 'right'] as const).find((x) => x === str(a.align));
      if (align) layer.align = align;
      if (typeof a.uppercase === 'boolean')
        layer.textTransform = a.uppercase ? 'uppercase' : 'none';
    } else if (layer.type === 'shape' && str(a.color)) {
      layer.fill = fillOf(a.color, this.style.accent);
    }
    // Animation: the moves given replace the old ones; a new delay moves the entrance
    const scene = this.scenes.find((s) => s.id === layer.motion?.scene);
    if (video && scene && layer.motion) {
      const m = this.motion(a, scene);
      if (m.enter) layer.motion.enter = m.enter;
      else if (a.delay_ms !== undefined && layer.motion.enter)
        layer.motion.enter.delay = clamp(num(a.delay_ms, 0), 0, scene.duration * 0.6);
      if (m.exit) layer.motion.exit = m.exit;
      if (m.emphasis) layer.motion.emphasis = m.emphasis;
      if (m.split) layer.motion.split = m.split;
    }
    return {
      ok: true,
      id,
      summary: `${layer.name}: updated${layer.type === 'text' ? ` «${layer.text.slice(0, 30)}»` : ''}`,
    };
  }

  /** Layers of a scene (a banner has one scene: all of them), bottom first. */
  private sceneLayers(scene: SceneDraft): Layer[] {
    return this.mode === 'video'
      ? this.layers.filter((l) => l.motion?.scene === scene.id)
      : this.layers;
  }

  /** Colour under a point of a scene: its background, then shapes and photos above it. */
  private colorUnder(scene: SceneDraft, below: Layer, x: number, y: number): string {
    let rgb = hexRgb(fillColor(scene.background, this.style.background));
    for (const l of this.sceneLayers(scene)) {
      if (l === below) break;
      if (l.type === 'text' || x < l.x || y < l.y || x > l.x + l.width || y > l.y + l.height)
        continue;
      if (l.type === 'image') rgb = [128, 128, 128];
      else if (l.type === 'shape' && l.fill) {
        const c = fillColor(l.fill, '#000000');
        rgb = blend(rgb, hexRgb(c), hexAlpha(c) * l.opacity);
      }
    }
    return rgbHex(rgb);
  }

  /**
   * The art director's checks: every problem a viewer would notice, with the id to fix it.
   * Estimates (glyph widths, reading speed) err on the lenient side: a remark must be real.
   */
  review(): string[] {
    const issues: string[] = [];
    const video = this.mode === 'video';
    const seen = new Map<string, string>();
    for (const scene of this.scenes) {
      const layers = this.sceneLayers(scene);
      const texts = layers.filter((l): l is Extract<Layer, { type: 'text' }> => l.type === 'text');
      const where = video ? ` in ${scene.id}` : '';
      for (const t of texts) {
        const label = `${t.id} (${t.name} «${t.text.slice(0, 32)}»)`;
        // Fit: auto-fit shrinks long texts; shrunk below ~65 % they stop being headlines
        const pad = t.background ? t.padding.left + t.padding.right : 0;
        const k = fitScale(t, t.width - pad, t.height);
        if (k < 0.65)
          issues.push(
            `${label}: too long for its box, it would shrink to ~${Math.round(k * 100)}% of its size — shorten it, or enlarge the box`,
          );
        // Contrast with what is behind it (its own pill, or the scene under its centre)
        const behind =
          t.background && t.background.type === 'solid'
            ? t.background.color
            : this.colorUnder(scene, t, t.x + t.width / 2, t.y + t.height / 2);
        const ratio = contrastRatio(t.color, behind);
        const large = t.fontSize >= this.u * 0.045;
        if (ratio < (large ? 3 : 4.5))
          issues.push(
            `${label}: hard to read on ${behind} (contrast ${ratio.toFixed(1)}:1) — change its colour, or put a panel or shade behind it`,
          );
        // The same line twice reads as filler
        const key = t.text
          .toLowerCase()
          .replace(/[^\p{L}\p{N}]+/gu, ' ')
          .trim();
        if (key.length > 3 && seen.has(key))
          issues.push(`${label}: repeats ${seen.get(key)} — every line should say something new`);
        else seen.set(key, t.id);
        // Arriving too late to be read
        const enter = t.motion?.enter;
        if (video && enter && enter.delay + enter.duration > scene.duration * 0.75)
          issues.push(
            `${label}: appears at ${((enter.delay + enter.duration) / 1000).toFixed(1)} s of a ${(scene.duration / 1000).toFixed(1)} s scene — too late to read; lower delay_ms`,
          );
      }
      // Two texts on top of each other
      for (let i = 0; i < texts.length; i++)
        for (let j = i + 1; j < texts.length; j++) {
          const [p, q] = [texts[i], texts[j]];
          if (p.motion?.exit || q.motion?.exit) continue;
          const ix = Math.min(p.x + p.width, q.x + q.width) - Math.max(p.x, q.x);
          const iy = Math.min(p.y + p.height, q.y + q.height) - Math.max(p.y, q.y);
          const small = Math.min(p.width * p.height, q.width * q.height);
          if (ix > 0 && iy > 0 && (ix * iy) / small > 0.25)
            issues.push(`${p.id} and ${q.id} overlap${where} — move one of them (update box)`);
        }
      if (video) {
        const graphics = this.fx.filter((f) => f.sceneId === scene.id);
        if (!texts.length && !graphics.length)
          issues.push(
            `${scene.id} (${scene.name}) shows only its background — give it a line or a graphic`,
          );
        // Reading time: ~3.5 words a second plus a beat to notice
        const words = texts.reduce((n, t) => n + t.text.split(/\s+/).filter(Boolean).length, 0);
        const need = 700 + words * 280;
        if (scene.duration < need)
          issues.push(
            `${scene.id} (${scene.name}) has ${words} words for ${(scene.duration / 1000).toFixed(1)} s — make it ${(need / 1000).toFixed(1)} s (update_scene) or cut words`,
          );
      }
    }
    // The ending asks for something
    const last = this.scenes[this.scenes.length - 1];
    if (last && (video ? this.scenes.length > 1 : true)) {
      const hasCta = this.sceneLayers(last).some((l) => l.type === 'text' && /^cta/.test(l.name));
      if (!hasCta)
        issues.push(
          `${video ? `the last scene ${last.id}` : 'the banner'} has no call to action — add a cta text`,
        );
    }
    return issues;
  }

  private addPhotoLayer(
    scene: SceneDraft,
    photo: string,
    b: { x: number; y: number; w: number; h: number },
    radius: number,
    shade: number,
    kenBurns: boolean,
    a: Record<string, unknown>,
  ): string {
    const video = this.mode === 'video';
    const p = PHOTOS[photo as keyof typeof PHOTOS] as { focusX?: number; focusY?: number };
    const m = video ? this.motion(a, scene) : undefined;
    if (m && kenBurns) {
      const { start, end } = this.sceneTimes(scene);
      m.keys = [
        { t: start, scale: 1.12, easing: 'linear' },
        { t: end, scale: 1.02 },
      ];
    }
    const layer = createLayer('image', {
      name: this.name_('photo'),
      src: `${GALLERY_SRC_PREFIX}${photo}`,
      fit: 'cover',
      focusX: p.focusX ?? 0.5,
      focusY: p.focusY ?? 0.5,
      borderRadius: Math.round((this.u * clamp(radius, 0, 50)) / 100),
      x: Math.round(b.x),
      y: Math.round(b.y),
      width: Math.round(b.w),
      height: Math.round(b.h),
      motion: m,
    } as Partial<Layer>);
    this.push(layer);
    if (shade > 0)
      this.push(
        createLayer('shape', {
          name: this.name_('shade'),
          shape: 'rectangle',
          fill: {
            type: 'linear',
            angle: 180,
            stops: [
              {
                offset: 0,
                color: `#000000${Math.round(shade * 120)
                  .toString(16)
                  .padStart(2, '0')}`,
              },
              {
                offset: 1,
                color: `#000000${Math.round(shade * 255)
                  .toString(16)
                  .padStart(2, '0')}`,
              },
            ],
          },
          x: Math.round(b.x),
          y: Math.round(b.y),
          width: Math.round(b.w),
          height: Math.round(b.h),
          motion: video ? { scene: scene.id } : undefined,
        } as Partial<Layer>),
      );
    return layer.id;
  }

  /** Scene start and end on the video timeline (as planned so far). */
  private sceneTimes(scene: SceneDraft) {
    const planned = sceneTimeline(this.motionBase()).scenes.find((s) => s.id === scene.id);
    return { start: planned?.start ?? 0, end: planned?.end ?? scene.duration };
  }

  private motionBase(): DocMotion {
    const scenes: MotionScene[] = this.scenes.map((s, i) => ({
      id: s.id,
      name: s.name,
      duration: s.duration,
      transition:
        i < this.scenes.length - 1 && s.transition
          ? { preset: s.transition, duration: s.transition === 'cut' ? 16 : 600 }
          : null,
    }));
    return {
      duration: scenes.reduce((a, s) => a + s.duration, 0) || 3000,
      fps: 30,
      scenes,
      fx: [],
      camera: [],
      loops: 1,
      tracks: [],
      duck: 0.3,
    } as DocMotion;
  }

  private push(layer: Layer) {
    if (this.layers.length < 400) this.layers.push(layer);
  }

  /** What has been made so far, for the model ("state"). */
  status(): string {
    const length =
      this.mode === 'video'
        ? `; ${(this.totalMs() / 1000).toFixed(1)} s${this.targetMs ? ` of ${this.targetMs / 1000} s asked` : ''}`
        : '';
    return `${this.scenes.length} scene(s): ${this.scenes.map((s) => s.id).join(', ') || 'none'}${length}; ${this.layers.length} layers; ${this.fx.length} graphics`;
  }

  /** Length of the video so far (scenes minus transition overlaps), ms. */
  private totalMs(): number {
    return this.scenes.length ? sceneTimeline(this.motionBase()).duration : 0;
  }

  /** Stretches or shrinks the scenes so the video lasts the length asked for. */
  private fitLength() {
    if (!this.targetMs || !this.scenes.length) return;
    // Transitions overlap by a share of the scenes, so a few passes converge
    for (let i = 0; i < 4; i++) {
      const total = this.totalMs();
      if (Math.abs(total - this.targetMs) < 100) return;
      const k = this.targetMs / total;
      for (const s of this.scenes) s.duration = Math.round(clamp(s.duration * k, 1200, 12_000));
    }
  }

  /**
   * Colours and fonts taken from the brand kit become brand references: the template follows the
   * kit when it changes (a new primary colour recolours it).
   */
  private linkBrand() {
    const kit = this.brand;
    if (!kit) return;
    const bySlot = new Map<string, BrandColorSlot>();
    for (const slot of BRAND_COLOR_SLOTS) {
      const c = kit.colors[slot];
      if (c && !bySlot.has(c.toLowerCase())) bySlot.set(c.toLowerCase(), slot);
    }
    const heading = kit.fonts['font-heading']?.family;
    const body = kit.fonts['font-body']?.family;
    const link = (v: unknown, key: string): unknown => {
      if (Array.isArray(v)) return v.map((x) => link(x, key));
      if (v && typeof v === 'object') {
        const out: Record<string, unknown> = {};
        for (const [k, x] of Object.entries(v)) out[k] = link(x, k);
        return out;
      }
      if (typeof v !== 'string' || v.startsWith('brand:')) return v;
      if (/^color\d*$|Color$/.test(key)) {
        const slot = bySlot.get(v.toLowerCase());
        return slot ? `brand:${slot}|${v}` : v;
      }
      if (key === 'fontFamily' || key === 'font') {
        if (heading && v === heading) return `brand:font-heading|${v}`;
        if (body && v === body) return `brand:font-body|${v}`;
      }
      return v;
    };
    this.layers = this.layers.map((l) => link(l, '') as Layer);
    this.fx = this.fx.map((f) => link(f, '') as (typeof this.fx)[number]);
    this.scenes = this.scenes.map((sc) => ({
      ...sc,
      background: link(sc.background, '') as SceneDraft['background'],
    }));
  }

  build(): ComposeResult {
    if (!this.scenes.length)
      this.run({ name: 'add_scene', args: { name: 'Main', background: this.style.background } });
    const video = this.mode === 'video';
    const firstBg = this.scenes[0].background;
    const background: Fill =
      'photo' in firstBg ? { type: 'solid', color: this.style.background } : firstBg;
    this.linkBrand();
    if (!video) {
      const doc = templateDocSchema.parse({
        width: this.W,
        height: this.H,
        background,
        layers: this.layers.map(({ motion: _m, ...l }) => l),
      });
      return { doc, name: this.name || 'AI banner', music: null };
    }
    this.fitLength();
    const base = this.motionBase();
    const { scenes } = sceneTimeline(base);
    const times = new Map(scenes.map((s) => [s.id, s]));
    // Retime keys of photos now that every scene is known
    for (const l of this.layers) {
      const sid = l.motion?.scene;
      const t = sid ? times.get(sid) : undefined;
      if (t && l.motion?.keys?.length === 2 && l.type === 'image') {
        l.motion.keys[0].t = t.start;
        l.motion.keys[1].t = t.end;
      }
      // Animations fit inside their scene
      const m = l.motion;
      if (m && t) {
        const D = t.end - t.start;
        if (m.enter)
          m.enter.delay = Math.min(m.enter.delay, Math.max(0, D - m.enter.duration - 200));
        if (m.emphasis && m.emphasis.delay + m.emphasis.duration * m.emphasis.repeat > D)
          m.emphasis = null;
      }
    }
    const total = scenes[scenes.length - 1].end;
    const fx = this.fx.map(({ sceneId, at, ...f }) => {
      const s = sceneId ? times.get(sceneId) : undefined;
      const from = s?.start ?? 0;
      const to = s?.end ?? total;
      const start = Math.min(from + at, Math.max(from, to - 200));
      return {
        ...f,
        start: Math.round(start),
        duration: Math.round(Math.max(200, Math.min(f.duration, to - start))),
      };
    });
    const camera = this.camera.map(({ sceneId, at, ...c }) => {
      const s = times.get(sceneId)!;
      const start = Math.min(s.start + at, s.end - 200);
      return {
        ...c,
        start: Math.round(start),
        duration: Math.round(Math.min(c.duration, s.end - start)),
      };
    });
    let doc: TemplateDoc = templateDocSchema.parse({
      width: this.W,
      height: this.H,
      background,
      layers: this.layers,
      motion: {
        ...base,
        fx,
        camera,
        poster: this.scenes.length > 1 ? this.scenes[0].id : undefined,
      },
    });
    if (this.sound) doc = applyAutoSfx(doc);
    return { doc, name: this.name || 'AI video', music: this.music };
  }
}

/** Offline stand-in for the model: a scripted composition from the brief (tests, no API key). */
export function mockComposerCalls(brief: string, mode: ComposeMode): ToolCall[] {
  const words = brief.replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
  const head = words.slice(0, 4).join(' ') || 'New launch';
  const sub = words.slice(4, 12).join(' ') || head;
  const calls: ToolCall[] = [
    {
      name: 'plan_style',
      args: {
        background: '#0E0E12',
        text: '#FFFFFF',
        accent: '#FF4A0D',
        accent2: '#2B3BFF',
        display_font: 'Inter',
        body_font: 'Inter',
      },
    },
  ];
  const text = (
    scene: string,
    role: string,
    value: string,
    box: object,
    size: number,
    extra: object = {},
  ) => calls.push({ name: 'add_text', args: { scene, role, text: value, box, size, ...extra } });
  if (mode === 'banner') {
    calls.push({ name: 'add_scene', args: { name: 'Banner', background: '#0E0E12 #2B1A3A' } });
    calls.push({
      name: 'add_shape',
      args: {
        scene: 's1',
        shape: 'ellipse',
        box: { x: 55, y: 5, w: 50, h: 50 },
        color: '#FF4A0D',
        opacity: 0.9,
      },
    });
    text('s1', 'headline', head, { x: 0, y: 30, w: 100, h: 30 }, 13);
    text('s1', 'subtitle', sub, { x: 0, y: 62, w: 100, h: 12 }, 4.5, { weight: 400 });
    text('s1', 'cta', 'Get started', { x: 30, y: 80, w: 40, h: 9 }, 4, {
      pill: '#FF4A0D',
      color: '#0E0E12',
    });
  } else {
    calls.push(
      {
        name: 'add_scene',
        args: { name: 'Hook', background: '#0E0E12', duration_ms: 2600, transition: 'whip-pan' },
      },
      {
        name: 'add_scene',
        args: { name: 'Idea', background: '#FF4A0D', duration_ms: 2400, transition: 'slices' },
      },
      {
        name: 'add_scene',
        args: {
          name: 'Details',
          background: 'mountain-lake',
          duration_ms: 2800,
          transition: 'zoom-in',
        },
      },
      {
        name: 'add_scene',
        args: { name: 'Call to action', background: '#0E0E12', duration_ms: 2600 },
      },
      {
        name: 'add_graphic',
        args: { kind: 'hud', scene: 's1', params: [{ key: 'label', value: head.toUpperCase() }] },
      },
    );
    text('s1', 'headline', head, { x: 0, y: 35, w: 100, h: 30 }, 14, {
      enter: 'letters-blur',
      split: 'char',
      effect_kind: 'slice-text',
    });
    calls.push({
      name: 'add_graphic',
      args: {
        kind: 'orbit',
        scene: 's1',
        behind: true,
        params: [{ key: 'shape', value: 'pills' }],
      },
    });
    text(
      's2',
      'headline',
      words.slice(0, 2).join(' ') || head,
      { x: 0, y: 30, w: 100, h: 40 },
      18,
      { effect_kind: 'words', effect_params: [{ key: 'bg', value: 'none' }] },
    );
    text('s3', 'subtitle', sub, { x: 0, y: 70, w: 100, h: 20 }, 6, {
      enter: 'lines-mask',
      split: 'line',
    });
    calls.push({ name: 'add_camera', args: { scene: 's3', preset: 'push-in', duration_ms: 2000 } });
    text('s4', 'headline', 'Ready when you are', { x: 0, y: 30, w: 100, h: 20 }, 10, {
      enter: 'zoom-out',
    });
    text('s4', 'cta', 'Get started', { x: 25, y: 60, w: 50, h: 9 }, 4.5, {
      pill: '#FF4A0D',
      enter: 'pop',
      emphasis: 'pulse',
      delay_ms: 500,
    });
    calls.push(
      { name: 'add_graphic', args: { kind: 'light-leak', scene: 's4' } },
      { name: 'set_music', args: { style: 'pulse', energy: 'normal' } },
    );
  }
  calls.push({ name: 'finish', args: { name: head, sound_effects: true } });
  return calls;
}
